import { useState } from "react";
import { Alert, App, Button, Card, Form, Input, Modal, Space, Tag, Typography } from "antd";
import { CopyOutlined, SafetyCertificateOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { RecoveryCodesDto, SecurityDto, TotpSetupDto } from "@audit-plan/shared";
import { api, ApiError } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { PageHead } from "../components/audit/PageHead";
import { PasswordField, usePasswordRules } from "../components/PasswordField";
import { useModalProps } from "../components/useModalProps";
import { McpSessionsCard } from "../components/McpSessionsCard";
import { palette } from "../theme";

/** Sicurezza account: secondo fattore TOTP (con codici di recupero) e cambio password. */
export default function SecurityPage() {
  const { t } = useTranslation();
  const { me, refresh } = useAuth();
  const { message } = App.useApp();
  const qc = useQueryClient();
  const modalProps = useModalProps(460);
  // sensitive-ok: regole della password, non si mostra
  const rules = usePasswordRules(me?.email);
  const sec = useQuery({ queryKey: ["security"], queryFn: () => api.get<SecurityDto>("/auth/security") });
  const [setup, setSetup] = useState<TotpSetupDto | null>(null);
  const [codes, setCodes] = useState<string[] | null>(null);
  const [modal, setModal] = useState<"regen" | "disable" | null>(null);
  const [pwdForm] = Form.useForm();
  const [codeForm] = Form.useForm();
  const [modalForm] = Form.useForm();
  const fail = (e: unknown) => void message.error(e instanceof ApiError ? e.message : t("a_loadFail"));
  const reload = async () => {
    await qc.invalidateQueries({ queryKey: ["security"] });
    await refresh();
  };
  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      void message.success(t("c_copied"));
    } catch {
      void message.warning(t("a_copyManual"));
    }
  };

  const start = useMutation({ mutationFn: () => api.post<TotpSetupDto>("/auth/totp/setup"), onSuccess: (r) => { setSetup(r); codeForm.resetFields(); }, onError: fail });
  const enable = useMutation({
    mutationFn: (code: string) => api.post<RecoveryCodesDto>("/auth/totp/enable", { code: code.replace(/\s+/g, "") }),
    onSuccess: async (r) => {
      setSetup(null);
      setCodes(r.recoveryCodes);
      void message.success(t("c_secEnabledDone"));
      await reload();
    },
    onError: fail,
  });
  const changePwd = useMutation({
    mutationFn: (v: { current: string; password: string }) => api.post("/auth/change-password", v),
    onSuccess: () => { pwdForm.resetFields(); void message.success(t("c_secPwdDone")); },
    onError: fail,
  });
  const modalOk = useMutation({
    mutationFn: async (v: { password: string; code?: string }) => {
      if (modal === "regen") return api.post<RecoveryCodesDto>("/auth/recovery-codes", { password: v.password, code: (v.code ?? "").replace(/\s+/g, "") });
      return api.post("/auth/totp/disable", { password: v.password });
    },
    onSuccess: async (r) => {
      if (modal === "regen") setCodes((r as RecoveryCodesDto).recoveryCodes);
      else void message.success(t("c_secDisabledDone"));
      setModal(null);
      await reload();
    },
    onError: fail,
  });

  const s = sec.data;
  const totp = s?.totp;
  return (
    <div style={{ maxWidth: 760 }}>
      <PageHead title={t("c_secTitle")} sub={<Typography.Text type="secondary">{t("c_secSub")}</Typography.Text>} />
      {me?.mfa.setupNeeded && <Alert type="warning" showIcon style={{ marginBottom: 16 }} message={t("c_secSetupNeeded")} />}

      <Card
        style={{ marginBottom: 16 }}
        loading={sec.isLoading}
        title={<Space><SafetyCertificateOutlined />{t("c_secTotp")}</Space>}
        extra={totp ? <Tag color={totp.enabled ? "success" : "default"}>{totp.enabled ? t("c_secTotpOn") : t("c_secTotpOff")}</Tag> : null}
      >
        {totp && (
          <Space direction="vertical" size={14} style={{ width: "100%" }}>
            <Typography.Text type="secondary">{totp.required ? t("c_secTotpRequired") : t("c_secTotpOptional")}</Typography.Text>

            {codes && (
              <Alert
                type="success"
                showIcon
                message={t("c_secRecoveryTitle")}
                description={
                  <Space direction="vertical" style={{ width: "100%" }}>
                    <span>{t("c_secRecoveryOnce")}</span>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(130px,1fr))", gap: 6, fontFamily: "var(--ap-mono)", fontSize: 15 }}>
                      {codes.map((c) => <div key={c}>{c}</div>)}
                    </div>
                    <Space wrap>
                      <Button icon={<CopyOutlined />} onClick={() => copy(codes.join("\n"))}>{t("c_secCopyAll")}</Button>
                      <Button type="primary" onClick={() => setCodes(null)}>{t("c_secSaved")}</Button>
                    </Space>
                  </Space>
                }
              />
            )}

            {!totp.enabled && !setup && (
              <Button type="primary" loading={start.isPending} onClick={() => start.mutate()}>{t("c_secEnable")}</Button>
            )}

            {!totp.enabled && setup && (
              <Space direction="vertical" size={12} style={{ width: "100%" }}>
                <div>{t("c_secStep1")}</div>
                <div>
                  <div style={{ fontSize: 12, color: palette.muted }}>{t("c_secKey")}</div>
                  <Space.Compact style={{ width: "100%" }}>
                    <Input readOnly value={setup.secret} style={{ fontFamily: "var(--ap-mono)", fontSize: 16, letterSpacing: 1 }} onFocus={(e) => e.target.select()} />
                    <Button icon={<CopyOutlined />} onClick={() => copy(setup.secret)}>{t("a_copy")}</Button>
                  </Space.Compact>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: palette.muted }}>{t("c_secUri")}</div>
                  <Space.Compact style={{ width: "100%" }}>
                    <Input readOnly value={setup.otpauthUri} style={{ fontSize: 13 }} onFocus={(e) => e.target.select()} />
                    <Button icon={<CopyOutlined />} onClick={() => copy(setup.otpauthUri)}>{t("a_copy")}</Button>
                  </Space.Compact>
                  <div style={{ fontSize: 12, color: palette.muted, marginTop: 4 }}>{t("c_secNoQr")}</div>
                </div>
                <div>{t("c_secStep2")}</div>
                <Form form={codeForm} layout="inline" onFinish={(v: { code: string }) => enable.mutate(v.code)} style={{ rowGap: 8 }}>
                  <Form.Item name="code" rules={[{ required: true, pattern: /^\s*\d{3}\s?\d{3}\s*$/, message: t("required") }]}>
                    <Input inputMode="numeric" autoComplete="one-time-code" maxLength={7} placeholder="123456" style={{ width: 150, fontSize: 18, letterSpacing: 3 }} />
                  </Form.Item>
                  <Button type="primary" htmlType="submit" loading={enable.isPending}>{t("c_secConfirm")}</Button>
                  <Button onClick={() => setSetup(null)}>{t("c_secCancel")}</Button>
                </Form>
              </Space>
            )}

            {totp.enabled && (
              <Space direction="vertical" size={10} style={{ width: "100%" }}>
                <div>{t("c_secRecoveryLeft", { n: s?.recoveryCodesLeft ?? 0 })}</div>
                <Space wrap>
                  <Button onClick={() => { modalForm.resetFields(); setModal("regen"); }}>{t("c_secRegen")}</Button>
                  {totp.canDisable ? (
                    <Button danger onClick={() => { modalForm.resetFields(); setModal("disable"); }}>{t("c_secDisable")}</Button>
                  ) : (
                    <Typography.Text type="secondary">{t("c_secDisableLocked")}</Typography.Text>
                  )}
                </Space>
              </Space>
            )}
          </Space>
        )}
      </Card>

      <Card title={t("c_secPwd")}>
        <Form form={pwdForm} layout="vertical" onFinish={(v: { current: string; password: string }) => changePwd.mutate({ current: v.current, password: v.password })} requiredMark={false} disabled={changePwd.isPending}>
          <Form.Item name="current" label={t("c_secPwdCurrent")} rules={[{ required: true, message: t("required") }]}>
            <Input.Password autoComplete="current-password" size="large" style={{ fontSize: 16 }} />
          </Form.Item>
          <Form.Item name="password" label={t("c_secPwdNew")} rules={rules}>
            {/* sensitive-ok: regole della password, non si mostra */}
            <PasswordField email={me?.email} />
          </Form.Item>
          <Form.Item
            name="confirm"
            label={t("a_passwordConfirm")}
            dependencies={["password"]}
            rules={[
              { required: true, message: t("required") },
              ({ getFieldValue }) => ({ validator: (_, val) => (!val || getFieldValue("password") === val ? Promise.resolve() : Promise.reject(new Error(t("a_pwdMismatch")))) }),
            ]}
          >
            <Input.Password autoComplete="new-password" size="large" style={{ fontSize: 16 }} />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={changePwd.isPending}>{t("c_secPwdSave")}</Button>
        </Form>
      </Card>

      {me && me.role !== "supplier" && (
        <div style={{ marginTop: 16 }}>
          <McpSessionsCard scope="mine" />
        </div>
      )}

      <Modal
        open={!!modal}
        onCancel={() => setModal(null)}
        onOk={() => modalForm.submit()}
        confirmLoading={modalOk.isPending}
        title={modal === "regen" ? t("c_secRegen") : t("c_secDisable")}
        okText={t("save")}
        cancelText={t("cancel")}
        okButtonProps={modal === "disable" ? { danger: true } : undefined}
        {...modalProps}
        destroyOnHidden
      >
        <Form form={modalForm} layout="vertical" onFinish={(v: { password: string; code?: string }) => modalOk.mutate(v)}>
          <Typography.Paragraph type="secondary">{modal === "regen" ? t("c_secRegenHelp") : t("c_secDisableAsk")}</Typography.Paragraph>
          <Form.Item name="password" label={t("a_password")} rules={[{ required: true, message: t("required") }]}>
            <Input.Password autoComplete="current-password" style={{ fontSize: 16 }} />
          </Form.Item>
          {modal === "regen" && (
            <Form.Item name="code" label={t("c_mfaCode")} rules={[{ required: true, message: t("required") }]}>
              <Input inputMode="numeric" autoComplete="one-time-code" maxLength={7} style={{ fontSize: 16 }} />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
}
